/**
 * Envoltorio seguro de localStorage.
 *
 * El acceso a storage puede fallar (modo privado, cookies bloqueadas, SSR), por
 * lo que todas las operaciones degradan a un almacén en memoria en lugar de
 * romper la aplicación.
 */

const NAMESPACE = 'edsuite-web';

const memoryFallback = new Map<string, string>();

function getDriver(): Storage | null {
  try {
    const driver = globalThis.localStorage;
    // Test de escritura: algunos navegadores exponen el objeto pero lo bloquean.
    const probeKey = `${NAMESPACE}:__probe__`;
    driver.setItem(probeKey, '1');
    driver.removeItem(probeKey);
    return driver;
  } catch {
    return null;
  }
}

const namespaced = (key: string): string => `${NAMESPACE}:${key}`;

export const storage = {
  getRaw(key: string): string | null {
    const namespacedKey = namespaced(key);
    const driver = getDriver();

    if (!driver) return memoryFallback.get(namespacedKey) ?? null;

    try {
      return driver.getItem(namespacedKey);
    } catch {
      return memoryFallback.get(namespacedKey) ?? null;
    }
  },

  setRaw(key: string, value: string): void {
    const namespacedKey = namespaced(key);
    memoryFallback.set(namespacedKey, value);

    try {
      getDriver()?.setItem(namespacedKey, value);
    } catch {
      // Sin espacio o bloqueado: el fallback en memoria ya guardó el valor.
    }
  },

  remove(key: string): void {
    const namespacedKey = namespaced(key);
    memoryFallback.delete(namespacedKey);

    try {
      getDriver()?.removeItem(namespacedKey);
    } catch {
      // Ignorado a propósito.
    }
  },

  getJson<T>(key: string, fallback: T): T {
    const raw = storage.getRaw(key);
    if (!raw) return fallback;

    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  },

  setJson(key: string, value: unknown): void {
    try {
      storage.setRaw(key, JSON.stringify(value));
    } catch {
      // Valor no serializable: se ignora para no romper el flujo.
    }
  },
} as const;
